import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import { Matrix } from 'transformation-matrix-js';
import PreventScrollToParents from './PreventScrollToParents';
import styles from '../Inference.module.css';

const getDefaultMat = () => Matrix.from(1, 0, 0, 1, 0, 0);

export default ({ imageSrc, info }) => {
  const canvasEl = useRef(null);
  const canvasWrapper = useRef(null);
  const image = useRef(null);
  const layoutParams = useRef({});
  const [imageLoad, changeImageLoad] = useState(null);
  const [dragging, changeDragging] = useState(false);
  const [zoomStart, changeZoomStart] = useState(null);
  const [zoomEnd, changeZoomEnd] = useState(null);
  const mousePosition = useRef({
    x: 0,
    y: 0,
  });
  const prevMousePosition = useRef({
    x: 0,
    y: 0,
  });
  const [mat, changeMat] = useState(getDefaultMat());
  const [zoomM, changeZoomM] = useState([0, 0, false]);

  const onKey = (e) => {
    if (e.keyCode === 32) {
      zoomIn({
        x: 0,
        y: 0,
        sw: 1,
        sh: 1,
      });
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  });

  useLayoutEffect(() => {
    if (image.current === null || image.current.alt !== imageSrc) {
      image.current = new Image();
      image.current.onload = () => {
        changeImageLoad(true);
      };
      image.current.onerror = () => {
        changeImageLoad(false);
      };
      image.current.src = imageSrc;
      image.current.alt = imageSrc;
    }
    if (imageLoad) {
      const context = drawCanvas();
      context.save();
      context.restore();
      changeImageLoad(null);
    }
  });

  useLayoutEffect(() => {
    if (canvasEl.current) {
      drawCanvas();
    }
  }, [info]);

  const drawCanvas = () => {
    const canvas = canvasEl.current;
    canvas.oncontextmenu = (e) => e.preventDefault();
    const canvasWrap = canvasWrapper.current;
    const { clientWidth, clientHeight } = canvasWrap;
    const fitScale = Math.max(
      image.current.naturalWidth / clientWidth,
      image.current.naturalHeight / clientHeight
    );
    const [iw, ih] = [
      image.current.naturalWidth / fitScale || 0,
      image.current.naturalHeight / fitScale || 0,
    ];
    canvas.width = iw;
    canvas.height = ih;
    const context = canvas.getContext('2d');
    context.save();
    context.transform(...mat.clone().inverse().toArray());
    layoutParams.current = {
      iw,
      ih,
      fitScale,
      canvasWidth: clientWidth,
      canvasHeight: clientHeight,
    };

    context.drawImage(image.current, 0, 0, iw, ih);
    return context;
  };

  const checkZoomStatus = (x, y) => {
    const sw = mat.a + (mat.a <= 0.05 ? 0.0005 : 0.05);
    const xw = x - iw * (mat.a <= 0.05 ? 0.00025 : 0.025);
    const xh = y - ih * (mat.a <= 0.05 ? 0.00025 : 0.025);
    return (
      (xw >= 0 && xh >= 0 && xw + iw * sw <= iw && xh + ih * sw <= ih) ||
      mat.a < 1
    );
  };

  const zoomIn = (point) => {
    const { x, y, sw } = point;
    const newMat = getDefaultMat();
    const [a, b] = zoomM;
    mat.translate(-a, -b);
    changeMat(newMat);
    const [mx, my, mw] = [x, y, sw];
    mat.setTransform(1, 0, 0, 1, mx, my).scale(mw, mw);
    changeZoomM([mx, my, checkZoomStatus(x, y)]);
    changeMat(mat.clone());
  };

  const zoomInPosNeg = (status) => {
    const [x, y] = zoomM;
    const sw = mat.a - (mat.a <= 0.05 ? 0.0005 : 0.05) * status;
    const xw = x + iw * (mat.a <= 0.05 ? 0.00025 : 0.025) * status;
    const xh = y + ih * (mat.a <= 0.05 ? 0.00025 : 0.025) * status;
    if (xw >= 0 && xh >= 0 && xw + iw * sw <= iw && xh + ih * sw <= ih) {
      zoomIn({
        x: xw,
        y: xh,
        sw,
        sh: sw,
      });
    } else if (mat.a < 1) {
      zoomIn({
        x: 0,
        y: 0,
        sw: 1,
        sh: 1,
      });
    }
  };

  const mouseEvents = {
    onMouseMove: (e) => {
      const { left, top } = canvasEl.current.getBoundingClientRect();
      prevMousePosition.current.x = mousePosition.current.x;
      prevMousePosition.current.y = mousePosition.current.y;
      mousePosition.current.x = e.clientX - left;
      mousePosition.current.y = e.clientY - top;
      const projMouse = mat.applyToPoint(
        mousePosition.current.x,
        mousePosition.current.y
      );
      const { iw, ih } = layoutParams.current;
      if (dragging) {
        if (
          ih * mat.a +
            mat.f +
            (prevMousePosition.current.y - mousePosition.current.y) <=
            ih &&
          iw * mat.a +
            mat.e +
            (prevMousePosition.current.x - mousePosition.current.x) <=
            iw &&
          mat.e + (prevMousePosition.current.x - mousePosition.current.x) >=
            0 &&
          mat.f + (prevMousePosition.current.y - mousePosition.current.y) >= 0
        ) {
          mat.translate(
            prevMousePosition.current.x - mousePosition.current.x,
            prevMousePosition.current.y - mousePosition.current.y
          );
          changeZoomM([
            mat.e + (prevMousePosition.current.x - mousePosition.current.x),
            mat.f + (prevMousePosition.current.y - mousePosition.current.y),
            checkZoomStatus(
              mat.e + (prevMousePosition.current.x - mousePosition.current.x),
              mat.f + (prevMousePosition.current.y - mousePosition.current.y)
            ),
          ]);
          changeMat(mat.clone());
        }
      }
      if (zoomStart) {
        changeZoomEnd(projMouse);
      }

      e.preventDefault();
    },
    onMouseDown: (e) => {
      e.preventDefault();
      if (e.button === 2) {
        return changeDragging(true);
      }
      const projMouse = mat.applyToPoint(
        mousePosition.current.x,
        mousePosition.current.y
      );
      if (e.button === 0) {
        changeZoomStart(projMouse);
        changeZoomEnd(projMouse);
      }
    },
    onMouseUp: (e) => {
      e.preventDefault();
      const projMouse = mat.applyToPoint(
        mousePosition.current.x,
        mousePosition.current.y
      );
      if (zoomStart) {
        const zoomEnd = projMouse;
        if (
          Math.abs(zoomStart.x - zoomEnd.x) > 0.0005 &&
          Math.abs(zoomStart.y - zoomEnd.y) > 0.0005
        ) {
          const { iw } = layoutParams.current;
          const maxScale = Math.max(
            (zoomEnd.x - zoomStart.x) / iw,
            (zoomEnd.y - zoomStart.y) / ih
          );
          if (maxScale > 0) {
            zoomIn({
              x: zoomStart.x,
              y: zoomStart.y,
              sw: maxScale,
              sh: maxScale,
            });
          } else {
            zoomIn({
              x: zoomEnd.x,
              y: zoomEnd.y,
              sw: -maxScale,
              sh: -maxScale,
            });
          }
        }

        changeZoomStart(null);
        changeZoomEnd(null);
      }
      if (e.button === 1 || e.button === 0) {
        return changeDragging(false);
      }
      if (e.button === 2) {
        return changeDragging(false);
      }
    },
    onMouseOut: (e) => {
      e.preventDefault();
      if (e.button === 1 || e.button === 0) {
        return changeDragging(false);
      }
      if (e.button === 2) {
        return changeDragging(false);
      }
    },
    onWheel: (e) => {
      if (e.deltaY > 1 && mat.a >= 0.00005) {
        zoomInPosNeg(1);
      } else if (e.deltaY < -1 && zoomM[2]) {
        zoomInPosNeg(-1);
      }
    },
  };

  const { iw, ih } = layoutParams.current;

  const zoomBox = !zoomStart
    ? null
    : {
        ...mat.clone().inverse().applyToPoint(zoomStart.x, zoomStart.y),
        w: (zoomEnd.x - zoomStart.x) / mat.a,
        h: (zoomEnd.y - zoomStart.y) / mat.d,
      };
  if (zoomBox) {
    if (zoomBox.w < 0) {
      zoomBox.x += zoomBox.w;
      zoomBox.w *= -1;
    }
    if (zoomBox.h < 0) {
      zoomBox.y += zoomBox.h;
      zoomBox.h *= -1;
    }
  }

  return (
    <div
      className={styles.canvasWrapper}
      style={{
        display: '-ms-flexbox',
        height: info && 'calc(100% - 100px)',
      }}
      ref={canvasWrapper}
    >
      {imageLoad === false ? (
        <div className={styles.imageNoData}>No Data</div>
      ) : (
        <div
          className="graphLoaderContainer"
          style={{
            width: 'unset',
            height: 'unset',
          }}
        >
          <div className="graphLoader" />
        </div>
      )}
      {imageSrc && (
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            cursor: dragging ? 'grabbing' : zoomStart ? 'zoom-out' : 'zoom-in',
          }}
        >
          {zoomBox !== null && (
            <div
              style={{
                position: 'absolute',
                border: '1px solid rgba(255,255,255,0.5)',
                pointerEvents: 'none',
                left: zoomBox.x,
                top: zoomBox.y,
                width: zoomBox.w,
                height: zoomBox.h,
              }}
            />
          )}
          <PreventScrollToParents {...mouseEvents}>
            <canvas ref={canvasEl} />
          </PreventScrollToParents>
        </div>
      )}
    </div>
  );
};
